import React, { useState, useRef, useMemo, useEffect } from 'react';
import LS from '../../styles/layoutStyled';
import CS from '../../styles/componentStyled';
import styled from 'styled-components';
import InputModal from '../InputModal';
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import { userIdState } from '../../atom/TokenAtom';
import { EntryListsDto, EntryListDto } from '../../model/room.dto';
import {
  blankDefaultMsgRecoil,
  errorMsgRecoil,
  confirmMsgRecoil,
  orderModifyStateRecoil,
} from '../../atom/ModalAtom';
import WorkNum from '../../component/WorkNum';
import { socketio } from '../../api/socket.api';
import eventbus from '../../api/eventbus';
import { roomAPI } from '../../api/axiosIndex';
import { Icon } from '../../component/Icon';

interface OrderModifyProps {
  pickLineId: number | null;
  onListUpdate: () => void;
}

const OrderModify = ({ pickLineId, onListUpdate }: OrderModifyProps) => {
  const blankMsg = useRecoilValue(blankDefaultMsgRecoil);
  const setError = useSetRecoilState(errorMsgRecoil);
  const setConfirm = useSetRecoilState(confirmMsgRecoil);

  const [userId, setUserId] = useRecoilState(userIdState);

  const setModalState = useSetRecoilState(orderModifyStateRecoil);

  const [entryLists, setEntryLists] = useState<EntryListsDto | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [modifyEntryLists, setModifyEntryLists] =
    useState<EntryListsDto | null>(null);

  const getEntryLists = async () => {
    const response = await roomAPI.getEntryLists(Number(pickLineId));

    for (let i = 0; i < response.data.length; i++) {
      try {
        const workNums = await roomAPI.getWorkNums(response.data[i].userId);
        const monthAddition = workNums.data[0].monthAddition;
        response.data[i].todayWorkNum = workNums.data[0].todayCycle;
        response.data[i].monthWorkNum = workNums.data[0].monthCycle;
        response.data[i].allWorkNum =
          workNums.data[0].monthCycle + monthAddition;
      } catch (err: any) {
        setError('탕수 조회에 실패하였습니다.');
      }
    }

    setEntryLists(response.data);
    setModifyEntryLists(response.data);
  };

  const handleItemClick = (index: number) => {
    setActiveIndex(index);
  };

  const moveItem = (direction: 'up' | 'down') => {
    if (entryLists && activeIndex !== null) {
      const newList = [...entryLists];
      const activeItem = newList[activeIndex];
      const swapIndex = direction === 'up' ? activeIndex - 1 : activeIndex + 1;

      if (swapIndex < 0 || swapIndex >= newList.length) return;

      newList[activeIndex] = newList[swapIndex];
      newList[swapIndex] = activeItem;

      setEntryLists(newList);
      setModifyEntryLists(newList);
      setActiveIndex(swapIndex);
    }
  };

  const handleConfirm = async () => {
    if (modifyEntryLists) {
      const updatedList = modifyEntryLists.map((item, index) => ({
        id: item.id,
        targetTurn: index + 1,
      }));
      try {
        await roomAPI.putOrderList(Number(pickLineId), updatedList);
        socketio.entry(Number(pickLineId));
        setModalState(false);
        onListUpdate();
        setConfirm('순번 수정이 완료되었습니다.');
      } catch {
        setError('순번 수정에 실패하였습니다.');
      }
    }
  };

  useEffect(() => {
    getEntryLists();
  }, []);

  return (
    <>
      <InputModal
        title="순번 수정"
        subText="기사 선택 후 상하 버튼으로 순번을 수정하세요."
        onCancel={() => setModalState(false)}
        onConfirm={handleConfirm}
      >
        <ul className="input-wrap">
          <li>
            <CtrlBox>
              <h2>기사 리스트</h2>
              <div className="ctrl-btn-box">
                <button
                  className="img-box"
                  onClick={() => moveItem('up')}
                  disabled={activeIndex === null || activeIndex === 0}
                >
                  <Icon name="ArrowTopBgIcon" size="100%" color="#D8D7DC" />
                </button>
                <button
                  className="img-box"
                  onClick={() => moveItem('down')}
                  disabled={
                    activeIndex === null ||
                    activeIndex === (entryLists ? entryLists.length - 1 : 0)
                  }
                >
                  <Icon name="ArrowBottomBgIcon" size="100%" color="#D8D7DC" />
                </button>
              </div>
            </CtrlBox>
            <ListBox>
              <CS.EntryAndExitCardListBox>
                {entryLists?.map((list, index) => (
                  <li
                    className={`${list.position === 'Borrowed' ? 'basic-member' : 'fix-member'} ${index === activeIndex ? 'active' : ''}`}
                    key={index}
                    onClick={() => handleItemClick(index)}
                  >
                    <div className="left-box">
                      <div className="title-box">
                        <h1>순번 {index + 1}</h1>
                        <UserInfoWrap>
                          <h2>{list.userNumber}</h2>
                          <p>{list.otherText}</p>
                        </UserInfoWrap>
                      </div>
                    </div>
                    <div className="right-box">
                      {list.position !== 'Borrowed' && (
                        <ul className="work-num-box">
                          <li>
                            <h4>오늘</h4>
                            <span className="num">{list.todayWorkNum}건</span>
                          </li>
                          <li>
                            <h4>실</h4>
                            <span className="num">{list.monthWorkNum}건</span>
                          </li>
                          <li>
                            <h4>누적</h4>
                            <span className="num">{list.allWorkNum}건</span>
                          </li>
                        </ul>
                      )}
                    </div>
                  </li>
                ))}
              </CS.EntryAndExitCardListBox>
            </ListBox>
          </li>
        </ul>
      </InputModal>
    </>
  );
};

export default OrderModify;

const CtrlBox = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;

  .ctrl-btn-box {
    display: flex;
    gap: 4px;

    button {
      width: 24px;
      &:disabled {
        opacity: 0.4;
      }
    }
  }
`;

const ListBox = styled.div`
  width: 100%;
  overflow-y: auto;
  height: calc(100vh - 260px);
`;

export const UserInfoWrap = styled.div`
  display: flex;
  flex-direction: column;
`;
